import { useState, useCallback, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getWindForecast, compassDirection } from '../services/weather';
import SailingGuide from './SailingGuide';

function ClickHandler({ onAddPoint }) {
  useMapEvents({
    click(event) {
      onAddPoint({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
}

/**
 * Initial bearing from `from` to `to`, in degrees (0-360, clockwise from north).
 */
function bearing(from, to) {
  const rad = (deg) => (deg * Math.PI) / 180;
  const φ1 = rad(from.lat);
  const φ2 = rad(to.lat);
  const Δλ = rad(to.lng - from.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Approx distance in km between two points (haversine). */
function distanceKm(from, to) {
  const R = 6371;
  const rad = (deg) => (deg * Math.PI) / 180;
  const dLat = rad(to.lat - from.lat);
  const dLng = rad(to.lng - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(from.lat)) * Math.cos(rad(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Attach heading (bearing to next waypoint) to every waypoint; preserves `wind`. */
function attachBearings(pts) {
  const withHeadings = pts.map((p, i) => {
    if (i < pts.length - 1) {
      const deg = bearing(p, pts[i + 1]);
      const km = Math.round(distanceKm(p, pts[i + 1]) * 10) / 10;
      const rel = p.wind ? windRelative(p.wind.directionDeg, deg) : { windRelDeg: null, windRel: null };
      return { ...p, bearingDeg: Math.round(deg), bearing: compassDirection(deg), nextKm: km, ...rel };
    }
    return { ...p, bearingDeg: null, bearing: null, nextKm: null, windRelDeg: null, windRel: null };
  });

  // Detect a tack or gybe at each junction waypoint (where two consecutive legs meet).
  return withHeadings.map((p, j) => {
    if (j === 0) return { ...p, maneuver: null };
    const hPrev = withHeadings[j - 1].bearingDeg;
    const windFromDeg = p.wind ? p.wind.directionDeg : null;
    return { ...p, maneuver: computeManeuver(hPrev, p.bearingDeg, windFromDeg) };
  });
}

/**
 * Classify the maneuver needed to go from the previous heading to the next heading
 * at a waypoint, given the wind direction (meteorological FROM direction).
 * - Returns 'tack' if the boat's heading crosses the wind-FROM line (bow through the wind).
 * - Returns 'gybe' if the heading crosses the wind-TO line (stern through the wind).
 * - Returns null if the wind stays on the same side (no crossing).
 */
function computeManeuver(hPrev, hNext, windFromDeg) {
  if (hPrev == null || hNext == null || windFromDeg == null) return null;
  // Signed turn from the previous heading to the next heading (positive = clockwise).
  const turn = ((((hNext - hPrev) % 360) + 540) % 360) - 180;
  if (Math.abs(turn) < 1) return null; // straight course, no wind crossing
  const windToDeg = (windFromDeg + 180) % 360;
  // Does the turn pass through a given compass line?
  const crosses = (lineDir) => {
    const gap = ((lineDir - hPrev) % 360 + 360) % 360;
    if (turn > 0) return gap > 0.5 && gap < turn;
    return gap > 360 + turn && gap < 359.5;
  };
  if (crosses(windFromDeg)) return 'tack';
  if (crosses(windToDeg)) return 'gybe';
  return null;
}

/** Reduce one forecast hour to a compact wind summary for storage. */
function windSummary(hour) {
  return {
    directionDeg: hour.directionDeg,
    direction: compassDirection(hour.directionDeg),
    speedKnots: hour.speedKnots,
    gustsKnots: hour.gustsKnots,
    precipProb: hour.precipProb,
    weatherCode: hour.weatherCode,
  };
}

/**
 * Wind direction RELATIVE to the boat's heading.
 * - windDeg: meteorological direction the wind comes FROM (0-360)
 * - headingDeg: direction the boat travels TOWARD (0-360)
 * Returns relative angle where 0 = headwind, +90 = starboard beam, -90 = port beam, 180 = run (tailwind).
 */
function windRelative(windDeg, headingDeg) {
  if (windDeg == null || headingDeg == null) {
    return { windRelDeg: null, windRel: null };
  }
  const rel = ((((windDeg - headingDeg) % 360) + 540) % 360) - 180;
  const abs = Math.abs(rel);
  const side = rel > 0 ? 'Starboard' : 'Port';
  let label;
  if (abs <= 22.5) {
    label = 'Head';
  } else if (abs <= 60) {
    label = `${side} close-hauled`;
  } else if (abs <= 80) {
    label = `${side} close reach`;
  } else if (abs <= 100) {
    label = `${side} beam reach`;
  } else if (abs <= 150) {
    label = `${side} broad reach`;
  } else {
    label = 'Run';
  }
  return { windRelDeg: Math.round(rel), windRel: label };
}

/**
 * Marker icon for a waypoint — numbered dot plus a gold wind arrow above
 * (the direction the wind is blowing TOWARD). The boat heading is shown
 * separately as a blue arrow at the midpoint of each leg.
 */
function waypointIcon(point, index) {
  const windFromDeg = point.wind && point.wind.directionDeg != null ? point.wind.directionDeg : null;
  const towardDeg = windFromDeg !== null ? (windFromDeg + 180) % 360 : null;
  // The arrow glyph ➤ points east by default; CSS rotate is clockwise (+90° = south).
  const windRotation = towardDeg !== null ? towardDeg - 90 : null;

  const windArrowHtml = windRotation !== null
    ? `<div style="position:absolute;top:-22px;left:50%;transform:translateX(-50%) rotate(${windRotation}deg);font-size:22px;color:#ffd76a;text-shadow:0 0 5px #000,0 0 8px #000;line-height:1;pointer-events:none;">➤</div>`
    : '';
  const maneuverHtml = point.maneuver
    ? `<div style="position:absolute;top:-7px;right:-10px;background:${point.maneuver === 'tack' ? '#ff5b5b' : '#ffb35c'};color:#fff;border:2px solid #fff;border-radius:50%;width:18px;height:18px;font-size:11px;line-height:14px;text-align:center;box-shadow:0 1px 3px #000;pointer-events:none;">${point.maneuver === 'tack' ? '⚡' : '↩'}</div>`
    : '';
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:32px;height:32px;">
      ${windArrowHtml}${maneuverHtml}
      <div style="
        background:#2563eb;
        color:white;
        border:2px solid white;
        border-radius:50%;
        width:28px;
        height:28px;
        display:flex;
        align-items:center;
        justify-content:center;
        font-weight:bold;
        box-shadow:0 1px 4px #555;
        position:absolute;
        left:2px;
        top:2px;
      ">${index + 1}</div>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

/**
 * Blue boat-heading arrow, placed at the midpoint of a route leg and
 * rotated to point toward the next waypoint (the boat's course).
 */
function boatArrowIcon(bearingDeg) {
  const rotation = bearingDeg - 90; // ➤ points east by default; CSS rotate is clockwise.
  return L.divIcon({
    className: '',
    html: `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;pointer-events:none;">
      <span style="transform:rotate(${rotation}deg);font-size:24px;color:#6fd3ff;text-shadow:0 0 5px #000,0 0 8px #000;line-height:1;display:inline-block;">➤</span>
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

export default function RouteMap() {
  const [route, setRoute] = useState([]);
  const [windLoadingIds, setWindLoadingIds] = useState({});
  const idRef = useRef(1);

  const refreshWind = useCallback(async (point) => {
    setWindLoadingIds((cur) => ({ ...cur, [point.id]: true }));
    try {
      const forecast = await getWindForecast(point.lat, point.lng);
      const wind = forecast.length ? windSummary(forecast[0]) : null;
      setRoute((cur) => attachBearings(cur.map((p) => (p.id === point.id ? { ...p, wind, windError: undefined } : p))));
    } catch (err) {
      setRoute((cur) => attachBearings(cur.map((p) => (p.id === point.id ? { ...p, windError: err.message } : p))));
    } finally {
      setWindLoadingIds((cur) => ({ ...cur, [point.id]: false }));
    }
  }, []);

  const addPoint = useCallback(async (point) => {
    const id = idRef.current++;
    const newPoint = { ...point, id };
    setRoute((cur) => attachBearings([...cur, newPoint]));
    await refreshWind(newPoint);
  }, [refreshWind]);

  /** Move a waypoint to a new lat/lng and refetch its wind. */
  const movePoint = useCallback((id, lat, lng) => {
    setRoute((cur) => attachBearings(cur.map((p) => (p.id === id ? { ...p, lat, lng } : p))));
    refreshWind({ id, lat, lng });
  }, [refreshWind]);

  /** Remove a specific waypoint (not just the last one). */
  const removePoint = useCallback((id) => {
    setRoute((cur) => attachBearings(cur.filter((p) => p.id !== id)));
  }, []);

  const undoLastPoint = useCallback(() => {
    setRoute((cur) => attachBearings(cur.slice(0, -1)));
  }, []);

  const clearRoute = useCallback(() => {
    setRoute([]);
    setWindLoadingIds({});
  }, []);

  const [locState, setLocState] = useState({ loading: false, error: null });

  /** Use the browser's geolocation to set the starting point (waypoint 1). Only runs when clicked. */
  const useCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocState({ loading: false, error: 'Geolocation is not supported by this browser.' });
      return;
    }
    setLocState({ loading: true, error: null });
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const id = idRef.current++;
        const newPoint = { id, lat: latitude, lng: longitude };
        // Insert at the front so it becomes waypoint 1 (the starting point).
        setRoute((cur) => attachBearings([newPoint, ...cur]));
        await refreshWind(newPoint);
        setLocState({ loading: false, error: null });
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED
            ? 'Location permission was denied. Enable it in your browser to use this.'
            : err.code === err.POSITION_UNAVAILABLE
              ? 'Your location is currently unavailable.'
              : 'Could not get your location. Please try again.';
        setLocState({ loading: false, error: msg });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [refreshWind]);

  return (
    <div className="rm-container">
      <h3 className="rm-title">{'\u{1F9ED}'} Route Planner</h3>
      <p className="rm-desc">Click the map to add waypoints, drag a marker to move it, or use the {'\u2715'} button to remove one. Use the green {'\u{1F4CD}'} button to start the route at your current location. Wind data is fetched for every waypoint.</p>

      <div className="rm-toolbar">
        <button className="rm-btn rm-loc-btn" onClick={useCurrentLocation} disabled={locState.loading}>
          {locState.loading ? 'Locating\u2026' : '\u{1F4CD} My location'}
        </button>
        <button className="rm-btn" onClick={undoLastPoint} disabled={route.length === 0}>
          {'\u{21A9}'} Undo
        </button>
        <button className="rm-btn" onClick={clearRoute} disabled={route.length === 0}>
          {'\u{1F5D1}'} Clear
        </button>
        <span className="rm-counter">{route.length} waypoint{route.length !== 1 ? 's' : ''}</span>
        <span className="rm-legend">
          <span className="rm-legend-wind">{'\u27A4'}</span> wind ·{' '}
          <span className="rm-legend-boat">{'\u27A4'}</span> boat
        </span>
      </div>
      {locState.error && <div className="rm-loc-error">{locState.error}</div>}

      <MapContainer
        center={[-33.42, 151.34]}
        zoom={11}
        style={{ height: 420, width: '100%', borderRadius: 14, border: '2px solid #f6c94544' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickHandler onAddPoint={addPoint} />
        {route.map((point, index) => (
          <Marker
            key={point.id}
            position={[point.lat, point.lng]}
            draggable={true}
            eventHandlers={{
              dragend: (event) => {
                const ll = event.target.getLatLng();
                movePoint(point.id, ll.lat, ll.lng);
              },
            }}
            title={`Waypoint \u00b7 wind: ${point.wind ? point.wind.speedKnots + ' kn' : 'fetching\u2026'}`}
            icon={waypointIcon(point, index)}
          />
        ))}
        {route.length >= 2 && (
          <Polyline
            positions={route.map((p) => [p.lat, p.lng])}
            pathOptions={{ color: '#2563eb', weight: 4 }}
          />
        )}
        {route.slice(0, -1).map((p, i) => {
          const next = route[i + 1];
          return (
            <Marker
              key={`boat-arrow-${p.id}`}
              position={[(p.lat + next.lat) / 2, (p.lng + next.lng) / 2]}
              interactive={false}
              zIndexOffset={-10}
              icon={boatArrowIcon(p.bearingDeg)}
            />
          );
        })}
      </MapContainer>

      {/* Route legs — heading between each pair */}
      {route.length >= 2 && (
        <div className="rm-legs">
          <h4 className="rm-legs-title">Route headings</h4>
          {route.slice(0, -1).map((p, i) => (
            <div key={p.id} className="rm-leg">
              <span className="rm-leg-nums">WP {i + 1} {'\u2192'} WP {i + 2}</span>
              <span className="rm-leg-dir">{p.bearing}</span>
              <span className="rm-leg-deg">{p.bearingDeg}{'\u00B0'}</span>
              <span className="rm-leg-km">{p.nextKm} km</span>
              {route[i].maneuver && (
                <span className={`rm-badge rm-badge-${route[i].maneuver}`}>
                  {route[i].maneuver === 'tack' ? '\u26A1 Tack' : '\u21A9 Gybe'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Waypoint wind conditions */}
      <div className="rm-legs">
        <h4 className="rm-legs-title">Waypoint conditions</h4>
        {route.length === 0 && <p className="rm-desc">No waypoints yet.</p>}
        {route.map((p, i) => (
          <div key={p.id} className="rm-leg rm-leg-wind">
            <span className="rm-leg-nums">WP {i + 1}</span>
            {p.maneuver && (
              <span className={`rm-badge rm-badge-${p.maneuver}`}>
                {p.maneuver === 'tack' ? '\u26A1 Tack' : '\u21A9 Gybe'}
              </span>
            )}
            {windLoadingIds[p.id]
              ? <span className="rm-leg-loading">Fetching wind{'\u2026'}</span>
              : p.wind
                ? (
                  <span className="rm-leg-dir">
                    {p.wind.direction} {p.wind.speedKnots || '\u2014'} kn
                    {p.wind.gustsKnots ? ` \u00b7 gusts ${p.wind.gustsKnots} kn` : ''}
                    {p.wind.precipProb ? ` \u00b7 rain ${p.wind.precipProb}%` : ''}
                    {p.windRelDeg !== null
                      ? <span className="rm-leg-rel">{'\u00b7'} rel: {p.windRel} ({p.windRelDeg > 0 ? '+' : ''}{p.windRelDeg}{'\u00B0'})</span>
                      : null}
                  </span>
                )
                : <span className="rm-leg-loading">{p.windError || 'No wind data'}</span>}
            <button
              className="rm-del-btn"
              onClick={() => removePoint(p.id)}
              title="Remove waypoint"
              disabled={route.length <= 1}
            >{'\u2715'}</button>
          </div>
        ))}
      </div>

      <details className="rm-raw">
        <summary>Raw route data</summary>
        <pre>{JSON.stringify(route, null, 2)}</pre>
      </details>

      {/* Sailing guide for each leg */}
      <SailingGuide legs={route.slice(0, -1)} />
    </div>
  );
}